import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../api/client'
import { quickAddOwner } from '../api/devices'
import {
  quickAddOffice,
  rememberOfficeLocation,
  retrieveUnassignedLogs,
  saveUnassignedCard,
  unassignedAssignQuery,
  unassignedCardQuery,
  unassignedDebugQuery,
  unassignedDetailsQuery,
  type UnassignedAssign,
  type UnassignedDebug,
  type UnassignedDetails,
} from '../api/unassignedCard'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import './UnassignedCard.css'

/**
 * Fisa unui aparat abia aparut, desfacuta sub randul lui din Unassigned -
 * `overview/unassigned/others/actionTabs.php` cu cele trei machete ale lui.
 *
 * DE CE TREI COLOANE SI NU TREI FILE (din macheta veche)
 *
 * Erau trei file una sub alta - Assign, Summary, Match - iar omul care lua in
 * primire o masina trebuia sa umble intre ele. Acum sunt trei coloane, in
 * ordinea in care se gandeste: CE e (stanga), CUM se poarta agentul pe ea
 * (mijloc), A CUI e (dreapta). Se citeste de la stanga la dreapta si se
 * termina cu butonul. Markupul si clasele (`ua-panes`, `ua-pane`, `ud-*`,
 * `dg-*`, `ua-learn`) sunt cele din portal; stilurile lor stau in
 * `UnassignedCard.css`.
 *
 * Fiecare coloana se cere singura, si una poate lipsi cu totul cand omul n-are
 * dreptul s-o vada - banda (`/card`) spune care sunt.
 */
export function UnassignedCard({
  id,
  onAssigned,
  inModal = false,
}: {
  id: number
  onAssigned?: (owner: number) => void
  /**
   * Fisa e deschisa in FEREASTRA, nu in pagina intreaga (`UnassignedFirst`).
   *
   * Numai o clasa in plus, si numai pentru asezare: in fereastra fisa isi
   * deruleaza singura continutul si bara de salvare se lipeste de capatul ei
   * (vezi `.ua-panes-wrap-modal` in foaie). Se spune, nu se ghiceste din
   * latime: aceeasi componenta deseneaza si pagina, si fereastra.
   */
  inModal?: boolean
}) {
  const band = useQuery(unassignedCardQuery(id))
  /* Starea Save-ului sta in AssignPane (acolo sunt campurile si `save()`);
     bara de jos o afla de aici, ca sa stie cand butonul poate fi apasat. */
  const [saveState, setSaveState] = useState<AssignSaveState>({ ready: false, busy: false })
  if (band.error instanceof ApiError) return <div className="alert alert-danger">{band.error.message}</div>
  if (!band.data) return <Spinner />
  const panes = band.data.panes
  /* Id-ul formularului e pe aparat: pot sta mai multe fise desfacute deodata. */
  const formId = `uaAssignForm-${id}`
  return (
    <div className={`ua-panes-wrap${inModal ? ' ua-panes-wrap-modal' : ''}`}>
      <div className="ua-panes">
        {panes.includes('details') && (
          <section className="ua-pane" data-pane="details">
            <h5 className="ua-pane-title">
              <span>
                <i className="fas fa-info-circle"></i> Details
              </span>
            </h5>
            <div className="ua-pane-body">
              <DetailsPane id={id} />
            </div>
          </section>
        )}
        {panes.includes('debug') && (
          <section className="ua-pane" data-pane="debug">
            <h5 className="ua-pane-title">
              <span>
                <i className="fas fa-sliders-h"></i> Settings &amp; debug
              </span>
            </h5>
            <div className="ua-pane-body">
              <DebugPane id={id} />
            </div>
          </section>
        )}
        {panes.includes('assign') && (
          <section className="ua-pane ua-pane-assign" data-pane="assign">
            <h5 className="ua-pane-title">
              <span>
                <i className="fas fa-user-check"></i> Assign
              </span>
            </h5>
            <div className="ua-pane-body">
              <AssignPane id={id} formId={formId} onAssigned={onAssigned} onSaveState={setSaveState} />
            </div>
          </section>
        )}
      </div>
      {/*
       * BARA DE SALVARE STA SUB TOATE COLOANELE, NU IN ASSIGN.
       *
       * In portal "Save settings" era la capatul formularului din Assign, deci
       * parea ca salveaza doar Assign - dar trimite si bifele din Settings &
       * debug. Butonul e centrat pe toata latimea fisei, ca sa nu para al
       * niciunei coloane (cerut de Bogdan). Trimite formularul din Assign prin
       * atributul `form`: acelasi `save()`, aceeasi garda, aceleasi mesaje.
       */}
      {panes.includes('assign') && (
        <div className="ua-save-bar">
          <button type="submit" form={formId} className="btn btn-primary" disabled={!saveState.ready || saveState.busy}>
            Add to CMDB
          </button>
        </div>
      )}
    </div>
  )
}

/** Ce afla bara de salvare despre formularul din Assign. */
type AssignSaveState = { ready: boolean; busy: boolean }

/* ----------------------------------------------------------------------------
 * Coloana din stanga - `unassignedDetails.php`: perechi cheie/valoare, grupate
 * dupa intrebare (masina, reteaua, agentul). Ce nu se stie NU se ascunde: se
 * scrie o liniuta.
 * ------------------------------------------------------------------------- */

function Dash() {
  return <span className="ud-none">&mdash;</span>
}

function Value({ text }: { text: string }) {
  return text.trim() === '' ? <Dash /> : <>{text}</>
}

/*
 * O coloana care n-a putut fi citita spune DE CE.
 *
 * Serverul prinde acum caderea fiecarei coloane si raspunde cu `why` in loc
 * sa iasa cu 500 si opt caractere de referinta - vezi `UnassignedCard::orSay`.
 * Aici se arata. Fara randul asta, raspunsul ar ajunge in componenta ca niste
 * date fara campuri si ecranul ar desena o fisa goala, ceea ce e mai rau
 * decat o eroare: pare ca aparatul chiar n-are nimic.
 */
function Why({ said }: { said: string }) {
  return <div className="alert alert-danger">{said}</div>
}

function Row({ k, children, className = '' }: { k: string; children: React.ReactNode; className?: string }) {
  return (
    <div className="ud-row">
      <span className="ud-k">{k}</span>
      <span className={`ud-v ${className}`.trim()}>{children}</span>
    </div>
  )
}

function DetailsPane({ id }: { id: number }) {
  const q = useQuery(unassignedDetailsQuery(id))
  if (q.error instanceof ApiError) return <div className="alert alert-danger">{q.error.message}</div>
  if (!q.data) return <div className="ua-loading">Loading...</div>
  if ('why' in q.data) return <Why said={String((q.data as unknown as { why: string }).why)} />
  const d: UnassignedDetails = q.data

  /* Adresa interna din ce stie masina - nu dintr-un singur camp (vezi macheta). */
  let primaryIp = d.internalIp
  let ipSource = primaryIp !== '' ? 'agent' : ''
  if (primaryIp === '' && d.addresses.length > 0) {
    primaryIp = d.addresses[0].ip
    ipSource = 'nic'
  }
  if (primaryIp === '' && d.scannedIp !== '') {
    primaryIp = d.scannedIp
    ipSource = 'scan'
  }
  const primaryNet = d.addresses.find((a) => a.ip === primaryIp)?.cidr ?? ''
  const others = d.addresses.filter((a) => a.ip !== primaryIp)

  return (
    <div className="ud">
      <div className="ud-group">
        <div className="ud-group-title">Machine</div>
        <Row k="Name" className="ud-strong">
          <Value text={d.name} />
        </Row>
        <Row k="Type">
          <Value text={d.type} />
          {d.virtual && <span className="ud-tag">virtual</span>}
        </Row>
        <Row k="Operating system">
          <Value text={d.os} />
          {d.osFamilyName !== '' && (
            <div className="ud-none" style={{ fontSize: 11 }}>
              {d.osFamilyName}
            </div>
          )}
        </Row>
        {d.osVersion !== '' && (
          <Row k="OS version" className="ud-mono">
            {d.osVersion}
          </Row>
        )}
        <Row k="Model">
          <Value text={d.model} />
        </Row>
        <Row k="Serial">
          <Value text={d.serial} />
        </Row>
      </div>

      <div className="ud-group">
        <div className="ud-group-title">Network</div>
        <Row k="Internal IP">
          <Value text={primaryIp} />
          {primaryNet !== '' && (
            <span className="ud-none" style={{ fontSize: 11 }}>
              {' '}
              ({primaryNet})
            </span>
          )}
          {ipSource === 'scan' && (
            <div className="ud-none" style={{ fontSize: 11 }}>
              seen by the network scan
            </div>
          )}
          {others.map((a) => (
            <div key={a.ip} className="ud-mono" style={{ fontSize: 11 }}>
              {a.ip}
              {a.cidr !== null && <span className="ud-none"> ({a.cidr})</span>}
            </div>
          ))}
        </Row>
        <Row k="External IP">
          <Value text={d.externalIp} />
        </Row>
        <Row k="Mac" className="ud-mono">
          <Value text={d.mac} />
        </Row>
        <Row k="Seen from">
          {d.location ? (
            <>
              <i className="fas fa-map-marker-alt ud-none"></i> {d.location.name}
              {/* PE CE S-A BAZAT: un sediu ghicit de pe adresa publica e alt fel
                  de sigur decat unul iesit dintr-o retea declarata pe sediu. */}
              <div className="ud-none" style={{ fontSize: 11 }}>
                from the {d.location.source === 'external' ? 'public' : 'internal'} address {d.location.matched}
              </div>
            </>
          ) : (
            <Dash />
          )}
        </Row>
      </div>

      <div className="ud-group">
        <div className="ud-group-title">Agent</div>
        <Row k="Version">
          <Value text={d.agentVersion} />
        </Row>
        <Row k="First registered">
          <Value text={d.firstRegistered} />
        </Row>
        <Row k="Last communication">
          <Value text={d.lastCommunication} />
        </Row>
        <Row k="Logged on">
          <Value text={d.loggedOn} />
        </Row>
      </div>

      {/* "Match" era o fila intreaga; aici e o propozitie care apare NUMAI cand
          chiar exista o potrivire - si atunci e cel mai important lucru de pe fisa. */}
      {d.matched > 0 && (
        <div className="ud-match">
          <i className="fas fa-clone"></i> <strong>{d.matched}</strong> device{d.matched > 1 ? 's' : ''} with an agent already
          carry this name.
          <div className="ud-match-hint">This may be the same machine, registered twice.</div>
        </div>
      )}

      {d.owner !== '' && (
        <div className="ud-row ud-owner">
          <span className="ud-k">Owner</span>
          <span className="ud-v">
            <i className="fas fa-user ud-none"></i> {d.owner}
          </span>
        </div>
      )}
    </div>
  )
}

/* ----------------------------------------------------------------------------
 * Coloana din mijloc - `deviceDebugSettings.php`: cum se poarta agentul, si
 * cum se depaneaza. Bifele pleaca odata cu Save-ul din dreapta (un singur
 * buton pe fisa); "Send request" nu salveaza nimic, CERE ceva de la masina.
 * ------------------------------------------------------------------------- */

/** Bifele coloanei din mijloc, citite de Save-ul din dreapta prin DOM - ca in portal (`$('.updateAgentSettings').serializeArray()`). */
const AGENT_FORM = 'updateAgentSettings'

function Check({ name, checked, onChange, label, hint }: { name: string; checked: boolean; onChange(on: boolean): void; label: string; hint: string }) {
  return (
    <label className="dg-check">
      {/* Portalul trece `.iCheckSettings` prin iCheck (`icheckbox_flat-red`,
          overview.js): patratul rosu din sprite, nu casuta browserului.
          Acelasi invelis ca `ICheck` din MultiRemoteActions. */}
      <div
        className={`icheckbox_flat-red${checked ? ' checked' : ''}`}
        style={{ position: 'relative', display: 'inline-block', verticalAlign: 'middle', flex: '0 0 auto' }}
      >
        <input
          name={name}
          value="1"
          type="checkbox"
          className="iCheckSettings"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, margin: 0, cursor: 'pointer' }}
        />
      </div>
      <span className="dg-check-text">
        {label}
        <em>{hint}</em>
      </span>
    </label>
  )
}

function DebugPane({ id }: { id: number }) {
  const q = useQuery(unassignedDebugQuery(id))
  const [autoUpdate, setAutoUpdate] = useState(false)
  const [beta, setBeta] = useState(false)
  const [adRelay, setAdRelay] = useState(false)
  const [email, setEmail] = useState('')
  const [sending, setSending] = useState(false)
  const data: UnassignedDebug | undefined = q.data
  useEffect(() => {
    if (!data) return
    setAutoUpdate(data.autoUpdate)
    setBeta(data.beta)
    setAdRelay(data.adRelay)
  }, [data])
  if (q.error instanceof ApiError) return <div className="alert alert-danger">{q.error.message}</div>
  if (!data) return <div className="ua-loading">Loading...</div>
  if ('why' in data) return <Why said={String((data as unknown as { why: string }).why)} />

  const sendLogs = (ev: FormEvent) => {
    ev.preventDefault()
    if (!window.confirm('Are you sure?')) return
    setSending(true)
    void retrieveUnassignedLogs(id, email)
      .then(() => {
        setEmail('')
        toast('Request sent!', 'Success', 'success')
      })
      .catch((err: unknown) => toast(err instanceof Error ? err.message : 'Error', 'Error', 'error'))
      .finally(() => setSending(false))
  }

  return (
    <>
      <form className={`form-horizontal ${AGENT_FORM}`} method="post" onSubmit={(e) => e.preventDefault()}>
        <input type="hidden" name="device_unique" value={id} />
        <div className="ud-group-title">Agent</div>
        <Check name="agent_auto_update" checked={autoUpdate} onChange={setAutoUpdate} label="Auto update" hint="The agent installs new versions on its own." />
        {!data.isLinux && (
          <Check name="agent_beta_version" checked={beta} onChange={setBeta} label="Beta version" hint="Takes releases before they are general. Test machines only." />
        )}
        <div className="ud-group-title dg-spaced">Directory</div>
        <Check name="ad_relay" checked={adRelay} onChange={setAdRelay} label="Act as AD relay" hint="This machine reads Active Directory for the whole site. One per site is enough." />
      </form>

      <div className="dg-tool">
        <div className="ud-group-title">Retrieve logs</div>
        <p className="dg-hint">The agent packs its logs and sends them to this address. Nothing is changed on the machine.</p>
        <form className="form-horizontal retrieveLogs" method="post" onSubmit={sendLogs}>
          <input type="hidden" name="device_unique" value={id} />
          <div className="dg-inline">
            <input className="form-control" type="email" name="logs_email" placeholder="name@company.com" required value={email} onChange={(e) => setEmail(e.target.value)} />
            <button type="submit" className="btn btn-default" disabled={sending}>
              Send request
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

/* ----------------------------------------------------------------------------
 * Coloana din dreapta - `deviceAssignSettings.php`: a cui e masina. Sediul
 * vine propus din adresa, cu notita din ce adresa a iesit; omul si sediul se
 * adauga CHIAR DE AICI; iar sediul poate invata adresele masinii.
 * ------------------------------------------------------------------------- */

function AssignPane({
  id,
  formId,
  onAssigned,
  onSaveState,
}: {
  id: number
  formId: string
  onAssigned?: (owner: number) => void
  onSaveState?: (state: AssignSaveState) => void
}) {
  const q = useQuery(unassignedAssignQuery(id))
  const client = useQueryClient()
  const [owner, setOwner] = useState('')
  const [area, setArea] = useState('')
  const [office, setOffice] = useState('')
  const [group, setGroup] = useState('')
  const [classified, setClassified] = useState('')
  const [owners, setOwners] = useState<{ id: number; text: string }[]>([])
  const [offices, setOffices] = useState<{ id: number; name: string }[]>([])
  const [newUser, setNewUser] = useState(false)
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [email, setEmail] = useState('')
  const [newOffice, setNewOffice] = useState(false)
  const [officeName, setOfficeName] = useState('')
  const [learnExt, setLearnExt] = useState(true)
  const [learnInt, setLearnInt] = useState(false)
  const [learnResult, setLearnResult] = useState<{ done: string[]; failed: string[] } | null>(null)
  const [learned, setLearned] = useState<{ external: boolean; internal: boolean }>({ external: false, internal: false })
  const [busy, setBusy] = useState(false)
  const data: UnassignedAssign | undefined = q.data

  useEffect(() => {
    /* O coloana care n-a putut fi citita vine ca `{why}`, fara liste - iar efectul
       ruleaza inaintea ramurii `Why` de mai jos: `data.areas.length` dobora tot
       ecranul ("ue.areas is undefined") in loc sa arate motivul. */
    if (!data || 'why' in data) return
    setOwners(data.owners ?? [])
    setOffices(data.offices ?? [])
    setOwner(data.ownerId > 0 ? String(data.ownerId) : '')
    setArea(data.areaId > 0 ? String(data.areaId) : (data.areas ?? []).length === 1 ? String(data.areas[0].id) : '')
    /* Ce a scris un om bate deducerea: daca aparatul ARE sediu, ala ramane. */
    let chosen = data.officeId > 0 ? data.officeId : (data.suggestedOffice?.id ?? 0)
    if (chosen < 1 && data.offices.length === 1) chosen = data.offices[0].id
    setOffice(chosen > 0 ? String(chosen) : '')
    setGroup(data.groupId > 0 ? String(data.groupId) : data.groups.length === 1 ? String(data.groups[0].id) : '')
    setClassified(data.classified)
    setNewUser(data.owners.length === 0)
    setNewOffice(data.offices.length === 0)
  }, [data])

  /* Butonul Save sta in bara de sub coloane: ii spunem cand formularul exista
     (coloana s-a citit) si cand o cerere e pe drum. */
  const ready = data !== undefined && !('why' in data)
  useEffect(() => {
    onSaveState?.({ ready, busy })
  }, [ready, busy, onSaveState])

  if (q.error instanceof ApiError) return <div className="alert alert-danger">{q.error.message}</div>
  if (!data) return <div className="ua-loading">Loading...</div>
  if ('why' in data) return <Why said={String((data as unknown as { why: string }).why)} />

  const guessed = data.officeId < 1 && data.suggestedOffice !== null && office === String(data.suggestedOffice.id)
  const fail = (err: unknown, fallback = 'Please try again.') => toast(err instanceof ApiError ? err.message : fallback, 'Error', 'error')

  const createUser = () => {
    if (first.trim() === '' || last.trim() === '' || email.trim() === '') {
      toast('First name, last name and email are required.', 'Add a user', 'info')
      return
    }
    setBusy(true)
    void quickAddOwner(id, first.trim(), last.trim(), email.trim())
      .then((r) => {
        setOwners((list) => (list.some((o) => o.id === r.id) ? list.map((o) => (o.id === r.id ? { ...o, text: r.text } : o)) : [...list, { id: r.id, text: r.text }]))
        setOwner(String(r.id))
        setFirst('')
        setLast('')
        setEmail('')
        setNewUser(false)
        toast(`${r.text} can be assigned.`, 'User added', 'success')
      })
      .catch((e: unknown) => fail(e))
      .finally(() => setBusy(false))
  }

  const createOffice = () => {
    if (officeName.trim() === '') {
      toast('Write a name for the office.', 'Add an office', 'info')
      return
    }
    setBusy(true)
    void quickAddOffice(officeName.trim())
      .then((r) => {
        setOffices((list) => (list.some((o) => o.id === r.id) ? list : [...list, { id: r.id, name: r.text }]))
        setOffice(String(r.id))
        setOfficeName('')
        setNewOffice(false)
        toast(`${r.text} can be assigned.`, 'Office added', 'success')
      })
      .catch((e: unknown) => fail(e))
      .finally(() => setBusy(false))
  }

  /* LEGAREA ADRESELOR DE SEDIU - buton propriu, nu pleaca odata cu Save:
     refuzurile modelului sunt chiar ce trebuie citit, negru pe alb. */
  const remember = () => {
    const external = learnExt && !learned.external ? data.learnExternal : ''
    const internal = learnInt && !learned.internal ? data.learnInternal : ''
    if (office === '') {
      toast('Choose an office first.', 'Recognise this office', 'info')
      return
    }
    if (external === '' && internal === '') {
      toast('Tick what should point at this office.', 'Recognise this office', 'info')
      return
    }
    setBusy(true)
    void rememberOfficeLocation(Number(office), external, internal)
      .then((r) => {
        setLearnResult(r)
        /* Ce s-a scris nu se mai poate scrie a doua oara. */
        if (r.done.length > 0) {
          setLearned({ external: learned.external || external !== '', internal: learned.internal || internal !== '' })
          if (external !== '') setLearnExt(false)
          if (internal !== '') setLearnInt(false)
        }
      })
      .catch((e: unknown) => fail(e))
      .finally(() => setBusy(false))
  }

  /* Un singur buton pe toata fisa: campurile din coloana de mijloc pleaca
     odata cu cele de aici, citite din formularul ei (ca in portal). */
  const save = (ev: FormEvent) => {
    ev.preventDefault()
    /* Office e obligatoriu. In portal `<select required>` oprea trimiterea din
       browser; lista de aici nu stie `required`, deci cererea pleca goala si
       serverul raspundea cu eroare. Se opreste aici, inainte de server. */
    if (office === '') {
      toast('Choose an office - the device cannot be saved without one.', 'Assign', 'info')
      document.getElementById('uaOffice')?.querySelector<HTMLButtonElement>('button.dropdown-toggle')?.focus()
      return
    }
    const fields: Record<string, string> = {
      device_unique: String(id),
      device_owner: owner,
      device_area: area,
      device_office: office,
      device_group: group,
      device_classified_info: classified,
    }
    const agent = document.querySelector<HTMLFormElement>(`form.${AGENT_FORM}`)
    if (agent) {
      for (const name of ['agent_auto_update', 'agent_beta_version', 'ad_relay']) {
        const box = agent.querySelector<HTMLInputElement>(`input[name="${name}"]`)
        if (box) fields[name] = box.checked ? '1' : ''
      }
    }
    setBusy(true)
    void saveUnassignedCard(id, fields)
      .then((r) => {
        void client.invalidateQueries({ queryKey: ['inventoree', 'unassigned'] })
        if (r.owner < 1) {
          /* Fara stapan masina RAMANE in NEW: se spune, in loc sa se prefaca ca a plecat. */
          toast('Saved. Without a user the device stays in NEW.', 'Saved', 'info')
          return
        }
        toast('Added to CMDB.', 'Success', 'success')
        onAssigned?.(r.owner)
      })
      .catch((e: unknown) => fail(e))
      .finally(() => setBusy(false))
  }

  const picker = (name: string, idAttr: string, value: string, onChange: (v: string) => void, options: { value: string; label: string }[]) => (
    <SelectPicker id={idAttr} name={name} className="form-control ua-pick" title="Select..." value={value} onChange={onChange} options={options} />
  )

  return (
    <>
      <div className="notifications-box"></div>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Assign</strong> settings{' '}
        </h4>
      </header>
      <div className="panel-body">
        <form id={formId} className="form-horizontal labelcustomize updateDeviceSettings" data-collabel="3" data-alignlabel="left" data-label="color" method="post" onSubmit={save}>
          <input type="hidden" className="form-control" name="device_unique" value={id} required />

          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">User</span>
            </label>
            <div className="col-sm-9">
              {picker('device_owner', 'uaOwner', owner, setOwner, owners.map((o) => ({ value: String(o.id), label: o.text })))}
              <a className="ua-add" href="#" id="uaAddUser" onClick={(e) => { e.preventDefault(); setNewUser((v) => !v) }}>
                <i className="fas fa-plus"></i> Add a user
              </a>
              <div className="ua-new-user" id="uaNewUser" hidden={!newUser}>
                <input type="text" className="form-control input-sm" id="uaFirst" placeholder="First name" autoComplete="off" value={first} onChange={(e) => setFirst(e.target.value)} />
                <input type="text" className="form-control input-sm" id="uaLast" placeholder="Last name" autoComplete="off" value={last} onChange={(e) => setLast(e.target.value)} />
                <input type="email" className="form-control input-sm" id="uaEmail" placeholder="Email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
                <button type="button" className="btn btn-sm btn-default" id="uaCreateUser" disabled={busy} onClick={createUser}>
                  Add
                </button>
              </div>
              <span className="help-block">
                <i className="fas fa-info-circle"></i> If user is not selected the device remains to unnasigned
              </span>
            </div>
          </div>

          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Area</span>
            </label>
            <div className="col-sm-9">
              {(data.areas ?? []).length === 0 ? (
                <div className="ua-empty">
                  <i className="fas fa-info-circle"></i> Nothing defined yet.
                </div>
              ) : (
                picker('device_area', 'uaArea', area, setArea, data.areas.map((a) => ({ value: String(a.id), label: a.name })))
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Office</span>
            </label>
            <div className="col-sm-9">
              {picker('device_office', 'uaOffice', office, setOffice, offices.map((o) => ({ value: String(o.id), label: o.name })))}
              <a className="ua-add" href="#" id="uaAddOffice" onClick={(e) => { e.preventDefault(); setNewOffice((v) => !v) }}>
                <i className="fas fa-plus"></i> Add an office
              </a>
              <div className="ua-new-office" id="uaNewOffice" hidden={!newOffice}>
                <input type="text" className="form-control input-sm" id="uaOfficeName" placeholder="Office name" autoComplete="off" value={officeName} onChange={(e) => setOfficeName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); createOffice() } }} />
                <button type="button" className="btn btn-sm btn-default" id="uaCreateOffice" disabled={busy} onClick={createOffice}>
                  Add
                </button>
              </div>

              {guessed && data.suggestedOffice && (
                <span className="text-muted small">
                  <i className="fas fa-magic"></i> Suggested from {data.suggestedOffice.source === 'external' ? 'the public address' : 'the internal address'}{' '}
                  <code>{data.suggestedOffice.matched}</code>. Change it if it is wrong - nothing is saved until you press Add to CMDB.
                </span>
              )}

              {(data.learnExternal !== '' || data.learnInternal !== '') && (
                <div className="ua-learn" id="uaLearn">
                  <div className="ua-learn-title">
                    <i className="fas fa-graduation-cap"></i> Recognise this office from now on
                  </div>
                  {data.learnExternal !== '' && (
                    <div className="ua-learn-row">
                      <SettingsCheck
                        id="uaLearnExternal"
                        value={data.learnExternal}
                        checked={learnExt}
                        disabled={learned.external}
                        onChange={setLearnExt}
                        label={
                          <>
                            Public address <code>{data.learnExternal}</code>
                          </>
                        }
                      />
                    </div>
                  )}
                  {data.learnInternal !== '' && (
                    <div className="ua-learn-row">
                      <SettingsCheck
                        id="uaLearnInternal"
                        value={data.learnInternal}
                        checked={learnInt}
                        disabled={learned.internal}
                        onChange={setLearnInt}
                        label={
                          <>
                            Internal network <code>{data.learnInternal}</code>
                          </>
                        }
                      />
                      {data.learnAssumed && <span className="ua-learn-warn">assumed /24 - the machine did not report a netmask</span>}
                    </div>
                  )}
                  <button type="button" className="btn btn-sm btn-default" id="uaRemember" disabled={busy} onClick={remember}>
                    Remember
                  </button>
                  <div className="ua-learn-note">
                    Internal networks repeat between branches, so tie one only when this office really owns it. Everything is saved for
                    this company alone.
                  </div>
                  {learnResult && (
                    <div className="ua-learn-result">
                      {learnResult.done.map((line) => (
                        <div key={line} className="ua-learn-done">
                          {line}
                        </div>
                      ))}
                      {learnResult.failed.map((line) => (
                        <div key={line} className="ua-learn-failed">
                          {line}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Group</span>
            </label>
            <div className="col-sm-9">
              {data.groups.length === 0 ? (
                <div className="ua-empty">
                  <i className="fas fa-info-circle"></i> Nothing defined yet.{' '}
                  <a href="/app/settings/customers/groups" target="_blank" rel="noopener" className="ua-add">
                    <i className="fas fa-plus"></i> Add a group
                  </a>
                </div>
              ) : (
                <>
                  {picker('device_group', 'uaGroup', group, setGroup, data.groups.map((g) => ({ value: String(g.id), label: g.name })))}
                  <a href="/app/settings/customers/groups" target="_blank" rel="noopener" className="ua-add">
                    <i className="fas fa-plus"></i> Add a group
                  </a>
                </>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Classified info</span>
            </label>
            <div className="col-sm-9">
              <textarea className="form-control" name="device_classified_info" value={classified} onChange={(e) => setClassified(e.target.value)}></textarea>
            </div>
          </div>

          {/* Butonul Save nu mai sta aici: e in bara de sub coloane (`ua-save-bar`),
              legat de formularul asta prin `form={formId}`. */}
        </form>
      </div>
    </>
  )
}
